import { useState, useEffect, useCallback } from 'react';
import {
  deductionsApi,
  STATUS_OPTIONS,
  TOOLS_OPTIONS,
} from '../api/deductionsApi';
import {
  DATE_PRESET_OPTIONS,
  getDatePresetRange,
} from '../../common/compensationDateUtils';
import { useExportJob } from '../../../../hooks/useExportJob';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { executePrintReport } from '../../../../utils/printReportUtil';

const REPORT_COLUMNS = [
  { key: 'company_name', label: 'Company' },
  { key: 'branch_name', label: 'Branch' },
  { key: 'department_name', label: 'Department' },
  { key: 'emp_id', label: 'Emp ID' },
  { key: 'emp_name', label: 'Employee Name' },
  { key: 'vno', label: 'Voucher No' },
  { key: 'v_date_formatted', label: 'Date' },
  { key: 'pay_head_name', label: 'Pay Head' },
  { key: 'amount', label: 'Amount', align: 'right', format: 'currency' },
];

export function useDeductionsList() {
  // Legacy Filter States
  const [datePreset, setDatePresetState] = useState('All');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [fromEmpId, setFromEmpId] = useState('');
  const [tillEmpId, setTillEmpId] = useState('');
  const [fromAmount, setFromAmount] = useState('');
  const [tillAmount, setTillAmount] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [payHeadFilter, setPayHeadFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Data & Pagination
  const [records, setRecords] = useState([]);
  const [dynamicOptions, setDynamicOptions] = useState({
    employees: [],
    payHeads: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Background Export Job Integration
  const { triggerExport, isExporting } = useExportJob({
    module: 'emp_deductions',
    jobType: 'EXPORT',
    fileFormat: 'XLSX',
  });

  // Load Dropdown Options
  useEffect(() => {
    deductionsApi
      .getDeductionOptions()
      .then((opts) => setDynamicOptions(opts))
      .catch((err) => console.error('Failed to load filter options:', err));
  }, []);

  // Update Date Preset handler
  const setDatePreset = (presetKey) => {
    setDatePresetState(presetKey);
    if (presetKey !== 'Custom') {
      const { fromDate: f, toDate: t } = getDatePresetRange(presetKey);
      setFromDate(f);
      setToDate(t);
    }
  };

  const handleFromDateChange = (val) => {
    setFromDate(val);
    setDatePresetState('Custom');
  };

  const handleToDateChange = (val) => {
    setToDate(val);
    setDatePresetState('Custom');
  };

  const getActiveFilterParams = useCallback(() => {
    return {
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      fromEmpId: fromEmpId ? Number(fromEmpId) : undefined,
      tillEmpId: tillEmpId ? Number(tillEmpId) : undefined,
      fromAmount: fromAmount ? Number(fromAmount) : undefined,
      tillAmount: tillAmount ? Number(tillAmount) : undefined,
      employee: employeeFilter.trim() || undefined,
      payHeadFilter: payHeadFilter ? Number(payHeadFilter) : undefined,
      status: statusFilter !== 'All' ? statusFilter : undefined,
    };
  }, [fromDate, toDate, fromEmpId, tillEmpId, fromAmount, tillAmount, employeeFilter, payHeadFilter, statusFilter]);

  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters = getActiveFilterParams();
      const data = await deductionsApi.getDeductions({
        ...filters,
        page,
        pageSize,
      });
      setRecords(data.items || []);
      setTotalCount(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Failed to load deductions:', err);
      appToast.error('Failed to load Deductions.');
    } finally {
      setIsLoading(false);
    }
  }, [getActiveFilterParams, page, pageSize]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleSearch = () => {
    setPage(1);
    loadRecords();
  };

  const resetFilters = () => {
    setDatePresetState('All');
    setFromDate('');
    setToDate('');
    setFromEmpId('');
    setTillEmpId('');
    setFromAmount('');
    setTillAmount('');
    setEmployeeFilter('');
    setPayHeadFilter('');
    setStatusFilter('All');
    setPage(1);
  };

  const handleStatusTabChange = (key) => {
    setStatusFilter(key);
    setPage(1);
  };

  const handleDelete = async (id, employee) => {
    const confirmed = await appConfirm({
      title: 'Delete Deduction Voucher',
      message: `Are you sure you want to delete the deduction record for ${employee || 'this employee'}? This action cannot be undone.`,
      confirmLabel: 'Delete Voucher',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      await deductionsApi.deleteDeduction(id);
      appToast.success('Deduction record deleted successfully.');
      loadRecords();
    } catch (err) {
      console.error('Delete error:', err);
      appToast.error('Failed to delete deduction.');
    }
  };

  const handleToolSelect = async (toolKey) => {
    const activeFilters = getActiveFilterParams();

    if (toolKey === 'print') {
      executePrintReport({
        title: 'Employee Deductions Report',
        columns: REPORT_COLUMNS,
        metadata: {
          'Date Range': fromDate && toDate ? `${fromDate} to ${toDate}` : (datePreset || 'All'),
          'Employee': employeeFilter || 'All',
        },
        fetchReportData: async () => {
          const res = await deductionsApi.getDeductionsReport(activeFilters);
          return { items: res?.items || [], total_amount: res?.total_amount || 0 };
        },
        onExportPdf: () => triggerExport(activeFilters, 'PDF'),
        onExportExcel: () => triggerExport(activeFilters, 'XLSX'),
        threshold: 1000,
      });
    } else if (toolKey === 'export_excel') {
      triggerExport(activeFilters, 'XLSX');
    } else if (toolKey === 'export_pdf') {
      triggerExport(activeFilters, 'PDF');
    }
  };

  return {
    state: {
      records,
      isLoading,
      datePreset,
      datePresetOptions: DATE_PRESET_OPTIONS,
      fromDate,
      toDate,
      fromEmpId,
      tillEmpId,
      fromAmount,
      tillAmount,
      employeeFilter,
      payHeadFilter,
      statusFilter,
      page,
      pageSize,
      totalCount,
      totalPages,
      statusOptions: STATUS_OPTIONS,
      tools: TOOLS_OPTIONS,
      dynamicOptions,
      isExporting,
    },
    actions: {
      setDatePreset,
      setFromDate: handleFromDateChange,
      setToDate: handleToDateChange,
      setFromEmpId,
      setTillEmpId,
      setFromAmount,
      setTillAmount,
      setEmployeeFilter,
      setPayHeadFilter,
      setStatusFilter,
      handleStatusTabChange,
      setPage,
      setPageSize,
      handleSearch,
      resetFilters,
      handleDelete,
      handleToolSelect,
      refresh: loadRecords,
    },
  };
}
